'use client';

import classNames from 'classnames';
import { useLocale, useTranslations } from 'next-intl';

import { CurrenciesFullNames } from '@/api/dictionary/getCurrenciesFullNames';
import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { Container } from '@/blocks/components/Container';
import typography from '@/styles/typography.module.scss';
import { capitalize } from '@/utils/string';

import styles from '@/app/[lang]/price-chart/[category]/[symbol]/styles.module.scss';
import { Asset as LocalAsset } from '@/app/[lang]/price-chart/[category]/types';

export function CoinPageSummarySection({
  asset,
  initialData,
  currenciesFullNames,
}: {
  currenciesFullNames: CurrenciesFullNames;
  asset: LocalAsset;
  initialData: RecapAsset[];
}) {
  const locale = useLocale();
  const tr = useTranslations();
  const { markets } = useRecap(undefined, initialData);

  if (!markets) {
    return null;
  }

  const assetMarketData: RecapAsset = {
    name: asset.name,
    description: asset.description,
    base: asset.base,
    quote: asset.quote,
    ...markets.find((a) => a.name === asset.name),
  } as RecapAsset;

  //prettier-ignore
  const keyName =
    (`priceChart${capitalize(asset.category)}.description`)
    + (asset.category === 'crypto' ? asset.quote === 'USD' ? '.usd' : '.coin': '')
    + (asset.category === 'crypto' && asset.quote === 'USD' ? assetMarketData?.marketCapInfo?.max_supply ? `.max` : `.noMax` : '');

  const dateFormatter = new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    year: '2-digit',
    day: '2-digit',
  });

  const { base, quote } = asset;

  const description = tr.rich(keyName, {
    assetName: () => currenciesFullNames[base],
    assetName2: () => currenciesFullNames[quote],
    assetTicker: () => base,
    assetTicker2: () => quote,
    currentDate: () => dateFormatter.format(Date.now()),
    coinInfoLast: () => <b>{assetMarketData?.price}</b>,
    coinInfoQuote: () => quote,
    coinInfoCapitalization: () => assetMarketData?.marketCapInfo?.market_cap,
    coinInfoTradingVolume: () => assetMarketData?.vol24,
    coinInfoCirculatingSupply: () => assetMarketData?.marketCapInfo?.circulating_supply,
    coinInfoTotalCount: () => assetMarketData?.marketCapInfo?.max_supply,
  });

  return (
    <section>
      <Container>
        <p className={classNames(typography.subheadingML, styles.summary)}>{description}</p>
      </Container>
    </section>
  );
}
